import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import Button from 'common/components/button';
import Dropzone, { DropzoneRef } from 'react-dropzone';
import HelloModal from 'common/components/hello-modal';
import hsFetch from 'hellospa/hs-fetch';
import loader from 'common/images/loader_@2x.gif';

import styles from './index.scss';

const messages = defineMessages({
  templateConverterHeader: {
    id: '', // ID's are procedurally generated
    description: 'page header, page lets user upload file to convert to template',
    defaultMessage: 'Upload your JSON or zip file to convert into template',
  },
  addFile: {
    id: '',
    description: 'button text, upload a file',
    defaultMessage: 'Add File',
  },
  dropFile: {
    id: '',
    description: 'section text, drag and drop a file to upload',
    defaultMessage: 'Drag and drop files here',
  },
  unsupportedFileType: {
    id: '',
    description: 'error message, file type is not supported',
    defaultMessage: "Sorry! We don't support that file type.",
  },
  confirmButton: {
    id: '',
    description: 'text for button that closes modal',
    defaultMessage: 'Okay',
  },
  modalHeaderSuccess: {
    id: '',
    description: 'modal header when file upload has succeeded',
    defaultMessage: 'Upload succeeded!',
  },
  modalHeaderError: {
    id: '',
    description: 'modal header when an error has occurred',
    defaultMessage: 'Upload failed',
  },
});

type Props = {
  csrfToken: string,
};

type SubmitState = {
  submitting: boolean,
  showModal: boolean,
  hasError: boolean,
  errorMsg?: string | null,
  isUploadedInvalidType: boolean,
};

type FileUploadResponse = {
  hasError: boolean,
  errorMsg: string | null,
};

export default function TemplateConverter(props: Props) {
  const { csrfToken } = props;
  const intl = useIntl();

  const [submitState, setSubmitState] = React.useState<SubmitState>({
    submitting: false,
    showModal: false,
    hasError: false,
    errorMsg: null,
    isUploadedInvalidType: false,
  });

  let dropzoneRef: DropzoneRef | null = null;

  const convertTemplate = async (file: File): Promise<FileUploadResponse> => {
    // Upload file from user's device
    const url = '/template/converter';

    const formData = new FormData();
    formData.append('file', file);
    formData.append('csrf_token', csrfToken);

    const response = await hsFetch(url, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    return {
      hasError: response.status !== 200,
      errorMsg: response.status !== 500
        ? data.error
        : null,
    };
  };

  const submit = async (files: File[]) => {
    if (files.length === 0) {
      setSubmitState((prevState) => ({
        ...prevState,
        isUploadedInvalidType: true,
      }));
      return;
    }

    setSubmitState((prevState) => ({
      ...prevState,
      isUploadedInvalidType: false,
      submitting: true,
      showModal: true,
      hasError: false,
      errorMsg: null,
    }));

    try {
      const data = await convertTemplate(files[0]);

      setSubmitState((prevState) => ({
        ...prevState,
        submitting: false,
        hasError: data.hasError,
        errorMsg: data.errorMsg,
      }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onRequestClose = () => {
    setSubmitState((prevState) => ({
      ...prevState,
      showModal: false,
    }));
  };

  const triggerFileInput = (evt: React.ChangeEvent<HTMLInputElement>) => {
    evt.preventDefault();
    if (dropzoneRef !== null) {
      dropzoneRef.open();
    }
  };

  const { showModal, hasError, errorMsg, submitting, isUploadedInvalidType } = submitState;
  const a1 = (msg: string) => (<a href='/home/manage?filter=template'>{msg}</a>);

  let modal;
  if (showModal) {
    const buttons = !submitting ? [
      {
        type: 'primary',
        text: intl.formatMessage(messages.confirmButton),
        onClick: onRequestClose,
      },
    ] : null;

    const successModalText = 'We\'re working on converting your files. As they complete, '
      + 'you\'ll be able to find them on your <a1>Templates page</a1>. Make sure to verify '
      + 'the templates have converted accurately before sending out a request.';

    let modalText = (
      <span>
        <FormattedMessage id='' description='modal success message, files uploaded successfully'
          defaultMessage={successModalText} values={{ a1 }} />
      </span>
    );

    if (hasError) {
      modalText = (
        errorMsg ? <span>
          <FormattedMessage id='' description='modal error, upload failed with error message'
            defaultMessage='Upload could not be completed: {errorMsg}.'
            values={{ errorMsg }} />
        </span> : <span>
          <FormattedMessage id='' description='modal error, upload failed with generic error'
            defaultMessage='Upload could not be completed. Please try your upload again.' />
        </span>
      );
    }

    const contentLabel = !submitting
      ? intl.formatMessage(hasError ? messages.modalHeaderError : messages.modalHeaderSuccess)
      : '';

    modal = (
      <HelloModal
        isOpen={true}
        onRequestClose={onRequestClose}
        contentLabel={contentLabel}
        showCloseIcon={false}
        buttons={buttons}
      >
        <div>{submitting
          ? <img className={styles.loaderIcon} src={loader} />
          : <p>{modalText}</p>}
        </div>
      </HelloModal>
    );
  }

  return (
    <div>
      <h1 className={styles.header}>
        {intl.formatMessage(messages.templateConverterHeader)}
      </h1>
      <div className={styles.uploadDivWrapper}>
        {modal}
        <div className={styles.container}>
          <Button
            className={styles.uploadButton}
            onClick={triggerFileInput}
            disabled={submitting}
            buttonText={intl.formatMessage(messages.addFile)} />
          <Dropzone
            noClick
            ref={(dz) => { dropzoneRef = dz; }}
            onDrop={submit}
            multiple={false}
            disabled={submitting}
            accept=".json,.zip"
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps({ className: styles.dropzoneContainer })}>
                <input {...getInputProps()} />
                <span className={styles.dropzoneLabel}>
                  {intl.formatMessage(messages.dropFile)}
                </span>
              </div>
            )}
          </Dropzone>
        </div>
      </div>
      {isUploadedInvalidType && (
        <span className={styles.fileTypeError}>
          {intl.formatMessage(messages.unsupportedFileType)}
        </span>
      )}
    </div>
  );
}
